import { useRef } from 'react';
import { Paperclip } from 'lucide-react';
import { uploadAttachment } from '../api/attachment';
import { ApiError } from '../api/client';
import { useComposerStaging } from './ComposerStagingContext';
import { useT } from '../I18nContext';

/**
 * Paperclip button + hidden file input. Click → native file
 * picker (constrained to JPG/PNG/WebP) → upload → stage in
 * the composer.
 *
 * Mobile note: the `accept="image/*"` plus no `capture` attr
 * lets mobile browsers offer both "Camera" and "Photo Library"
 * in their action sheet. Most users never need a separate
 * camera button on mobile because of this.
 *
 * One staged attachment at a time - pressing the button while
 * an attachment is staged just replaces it. Easier UX than
 * "delete the old one first".
 */
export function AttachButton() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { setStaged, setStatus, setErrorMessage, status } = useComposerStaging();
  const t = useT();

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so picking the SAME file twice fires onchange both
    // times. Without this, the change event short-circuits if the user
    // re-picks an identical file.
    e.target.value = '';
    if (!file) return;

    setStatus('uploading');
    setErrorMessage(null);
    try {
      const meta = await uploadAttachment(file);
      setStaged({ meta, displayName: file.name });
      setStatus('ready');
    } catch (err) {
      setStaged(null);
      setStatus('failed');
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : 'Could not upload that image. Please try again.',
      );
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFile}
        style={{ display: 'none' }}
      />
      <button
        type="button"
        className="composer-icon-btn"
        onClick={() => inputRef.current?.click()}
        disabled={status === 'uploading'}
        title={t('attach.tooltip')}
        aria-label={t('attach.tooltip')}
      >
        <Paperclip size={16} />
      </button>
    </>
  );
}
